import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as routing from '../../constants/Routing';
import { PLAN_TYPE } from '../../constants/Subscriptions';
import { ConfirmDeleteDialog } from '../../dialogs/ConfirmDeleteDialog';
import { ConstraintsDialog, ConstraintsType } from '../../dialogs/ConstraintsDialog';
import { ExportStopsReportsDialog } from '../../dialogs/ExportStopsReportsDialog';
import { ExportStopsTasksDialog } from '../../dialogs/ExportStopsTasksDialog';
import MoveStopsDialog from '../../dialogs/MoveStopsDialog';
import { StopsToDriverDialog } from '../../dialogs/StopsToDriverDialog';
import { StopsToSupervisorDialog } from '../../dialogs/StopsToSupervisorDialog';
import { Driver } from '../../domain/driver/Driver';
import { Stop } from '../../domain/stop/Stop';
import { StopFlat } from '../../domain/stop/StopFlat';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActivePlan } from '../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedStops } from '../../hooks/plan/useSetActivePlanSelectedStops';
import { useUpdateDriverStops } from '../../hooks/plan/useUpdateDriverStops';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useDeleteStops } from '../../hooks/stop/useDeleteStops';
import { useSaveStopsAsClients } from '../../hooks/stop/useSaveStopsAsClients';
import { useSetSelectedProjectStops } from '../../hooks/stop/useSetSelectedProjectStops';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import { MixPanelEvents } from '../../services/mixpanel';
import { ContextualMenu } from './ContextualMenu';

type StopsActionsMenuType = `contextual` | `popover`;

type StopsContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type StopsActionsMenuProps = {
  type?: StopsActionsMenuType;
  stops: Array<Stop | StopFlat>;
  contextAnchor?: StopsContextAnchor;
  showChangeConstraints?: boolean;
  showSaveAsClient?: boolean;
  showAssignToDriver?: boolean;
  showMoveToPlan?: boolean;
  showExportReports?: boolean;
  showExportTasks?: boolean;
  showViewDetails?: boolean;
  showDeleteStop?: boolean;
  showOpenPlan?: boolean;
  showOpenCustomers?: boolean;
  showAssignToSupervisor?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  setContextAnchor?: (context?: StopsContextAnchor) => void;
  afterDeleteStops?: (stopsIds: string[]) => void;
};

export const StopsActionsMenu = ({
  type = `contextual` as StopsActionsMenuType,
  stops,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showChangeConstraints = true,
  showSaveAsClient = true,
  showAssignToDriver = true,
  showMoveToPlan = true,
  showExportReports = true,
  showExportTasks = true,
  showViewDetails = true,
  showDeleteStop = true,
  showOpenPlan = false,
  showOpenCustomers = true,
  showAssignToSupervisor = true,
  popoverProps,
  setContextAnchor = () => {},
  afterDeleteStops,
}: StopsActionsMenuProps) => {
  const navigate = useNavigate();

  const { selectedStops: selectedProjectStopsIds, setSelectedStops: setProjectSelection } =
    useSetSelectedProjectStops();
  const { selectedStops, setSelectedStops } = useSetActivePlanSelectedStops();

  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { data: activePlan } = useRetrieveActivePlan();

  const { data: planStops } = useRetrieveActivePlanStops();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();

  const { setRightPanelInfo } = useRetrieveRightPanelInfo();

  const { run: updateDriverStops } = useUpdateDriverStops();

  const { run: deleteStops, success: deleteStopsSuccess, isLoading: deleteStopsLoading } = useDeleteStops();

  const { run: saveStopsAsClient } = useSaveStopsAsClients();
  const [dialog, setDialog] = useState<
    | `constraintsStops`
    | `assignToDriver`
    | `deleteStops`
    | `moveStops`
    | `exportReports`
    | `exportTasks`
    | `assignToSupervisor`
    | undefined
  >(undefined);

  useEffect(() => {
    if (deleteStopsSuccess) {
      setProjectSelection(
        selectedProjectStopsIds.filter(
          (selectedProjectStopsId) => !stops.map((stop) => stop.id).includes(selectedProjectStopsId)
        )
      );

      if (afterDeleteStops) {
        afterDeleteStops(stops.map((stop) => stop.id));
      }

      setSelectedStops(
        selectedStops.filter(
          (selectedActivePlanStopsId) => !stops.map((stop) => stop.id).includes(selectedActivePlanStopsId)
        )
      );
      setDialog(undefined);
      if (setContextAnchor) {
        setContextAnchor(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStopsSuccess]);
  if (stops.length === 0 && !deleteStopsLoading) return null;
  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="stops-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showViewDetails || stops.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `stop`,
                          detailId: `${stops[0].id}`,
                          detailTab: null,
                        });
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.view_details);
                      },
                      content: <Text intlId="services.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                    null,
                  ]),
              ...(!activeProject || !showOpenPlan || stops.length !== 1 || !stops[0].plan_id
                ? []
                : [
                    {
                      key: `open_in_plan`,
                      width: 240,
                      onClick: () => {
                        navigate(
                          `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(
                            `:projectId`,
                            `${activeProject!.id}`
                          ).replace(`:planId`, `${stops[0].plan_id}`)}?detailId=${stops[0].id}&detailType=stop`
                        );
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_plan);
                      },
                      content: <Text intlId="services.actions.open_in_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!activeProject || !showChangeConstraints || stops.length === 0
                ? []
                : [
                    {
                      key: `change_constraints`,
                      width: 240,
                      onClick: () => {
                        setDialog(`constraintsStops`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.constraints);
                      },
                      content: <Text intlId="services.actions.change_constraints" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showSaveAsClient
                ? []
                : [
                    {
                      key: `save_as_client`,
                      width: 240,
                      disabled: stops.some((s) => Boolean(s.client_id)),
                      onClick: () => {
                        saveStopsAsClient({ stopIds: stops.map((stop) => stop.id) });
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.save_as_client);
                      },
                      content: <Text intlId="services.actions.save_as_client" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showAssignToDriver
                ? []
                : [
                    {
                      key: `assign_stops_to_driver`,
                      width: 240,
                      onClick: () => {
                        setDialog(`assignToDriver`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_driver);
                      },
                      content: <Text intlId="services.actions.assign_stops_to_driver" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showMoveToPlan
                ? []
                : [
                    {
                      key: `move_stops_to_plan`,
                      width: 240,
                      onClick: () => {
                        setDialog(`moveStops`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.move_to_plan);
                      },
                      content: <Text intlId="services.actions.move_stops_to_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...((showExportReports && stops.length === 1) || (showExportTasks && activeProject?.tasks_enabled)
                ? [null]
                : []),
              ...(!showExportReports
                ? []
                : [
                    {
                      key: `export_reports`,
                      width: 240,
                      onClick: () => {
                        setDialog(`exportReports`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_reports);
                      },
                      content: <Text intlId="services.actions.export_reports" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showExportTasks || !activeProject?.tasks_enabled
                ? []
                : [
                    {
                      key: `export_tasks`,
                      width: 240,
                      onClick: () => {
                        setDialog(`exportTasks`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_tasks);
                      },
                      content: <Text intlId="services.actions.export_tasks" style={{ cursor: `pointer` }} />,
                    },
                  ]),

              ...((showOpenCustomers && stops.length === 1) ||
              (organization?.subscription.plan !== PLAN_TYPE.MONKEY && showAssignToSupervisor)
                ? [null]
                : []),

              ...(!showOpenCustomers || stops.length !== 1
                ? []
                : [
                    {
                      key: `open_in_customers`,
                      width: 240,
                      onClick: () => {
                        if (activeProject) {
                          window.open(`${activeProject.getCustomersDomain()}/stop/${stops[0].id}`, `_blank`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_customers);
                        }
                      },
                      content: <Text intlId="services.actions.open_in_customers" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(organization?.subscription.plan === PLAN_TYPE.MONKEY || !showAssignToSupervisor
                ? []
                : [
                    {
                      key: `assign_stops_to_supervisor`,
                      width: 240,
                      onClick: () => {
                        setDialog(`assignToSupervisor`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_supervisor);
                      },
                      content: (
                        <Text intlId="services.actions.assign_stops_to_supervisor" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showDeleteStop
                ? []
                : [
                    null,
                    {
                      key: `delete_service`,
                      width: 240,
                      onClick: () => {
                        setDialog(`deleteStops`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.delete);
                      },
                      content: (
                        <Text
                          intlId="services.actions.delete_service"
                          style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showViewDetails && stops.length === 1
                  ? [
                      {
                        key: `view_details`,
                        text: <Text intlId="services.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `stop`,
                            detailId: `${stops[0].id}`,
                            detailTab: null,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.view_details);
                        },
                      },
                      null,
                    ]
                  : []),
                ...(activeProject && showOpenPlan && stops.length === 1 && !!stops[0].plan_id
                  ? [
                      {
                        key: `open_in_plan`,
                        text: <Text intlId="services.actions.open_in_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          navigate(
                            `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(
                              `:projectId`,
                              `${activeProject!.id}`
                            ).replace(`:planId`, `${stops[0].plan_id}`)}?detailId=${stops[0].id}&detailType=stop`
                          );

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_plan);
                        },
                      },
                    ]
                  : []),
                ...(activeProject && showChangeConstraints && stops.length > 0
                  ? [
                      {
                        key: `change_constraints`,
                        disabled: stops.length === 0,
                        text: <Text intlId="services.actions.change_constraints" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`constraintsStops`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.constraints);
                        },
                      },
                    ]
                  : []),
                ...(showSaveAsClient
                  ? [
                      {
                        key: `save_as_client`,
                        disabled: stops.some((s) => Boolean(s.client_id)),
                        text: <Text intlId="services.actions.save_as_client" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          saveStopsAsClient({ stopIds: stops.map((stop) => stop.id) });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.save_as_client);
                        },
                      },
                    ]
                  : []),
                ...(showAssignToDriver
                  ? [
                      {
                        key: `assign_stops_to_driver`,
                        text: <Text intlId="services.actions.assign_stops_to_driver" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`assignToDriver`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_driver);
                        },
                      },
                    ]
                  : []),
                ...(showMoveToPlan
                  ? [
                      {
                        key: `move_stops_to_plan`,
                        text: <Text intlId="services.actions.move_stops_to_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`moveStops`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.move_to_plan);
                        },
                      },
                    ]
                  : []),
                ...((showExportReports && stops.length === 1) || (showExportTasks && activeProject?.tasks_enabled)
                  ? [null]
                  : []),
                ...(showExportReports
                  ? [
                      {
                        key: `export_reports`,
                        text: <Text intlId="services.actions.export_reports" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`exportReports`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_reports);
                        },
                      },
                    ]
                  : []),
                ...(showExportTasks && activeProject?.tasks_enabled
                  ? [
                      {
                        key: `export_tasks`,
                        text: <Text intlId="services.actions.export_tasks" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`exportTasks`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_tasks);
                        },
                      },
                    ]
                  : []),

                ...((showOpenCustomers && stops.length === 1) ||
                (organization?.subscription.plan !== PLAN_TYPE.MONKEY && showAssignToSupervisor)
                  ? [null]
                  : []),

                ...(showOpenCustomers && stops.length === 1
                  ? [
                      {
                        key: `open_in_customers`,
                        text: <Text intlId="services.actions.open_in_customers" style={{ cursor: `pointer` }} />,
                        onClick: () => {
                          if (activeProject) {
                            window.open(`${activeProject.getCustomersDomain()}/stop/${stops[0].id}`, `_blank`);
                            eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_customers);
                          }
                        },
                      },
                    ]
                  : []),
                ...(organization?.subscription.plan !== PLAN_TYPE.MONKEY && showAssignToSupervisor
                  ? [
                      {
                        key: `assign_stops_to_supervisor`,
                        text: (
                          <Text intlId="services.actions.assign_stops_to_supervisor" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`assignToSupervisor`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_supervisor);
                        },
                      },
                    ]
                  : []),
                ...(showDeleteStop
                  ? [
                      null,
                      {
                        key: `delete_service`,
                        text: (
                          <Text
                            intlId="services.actions.delete_service"
                            style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`deleteStops`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.delete);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="services"
        open={dialog === `deleteStops` || deleteStopsLoading}
        loading={deleteStopsLoading}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={stops}
        handleDelete={() => {
          let stopsToDelete = stops.map((s) => s.id);

          (stops as any[]).forEach((s) => {
            // If we are removing a delivery that has a pickup and pickup is inside the array, don't remove the pickup. It will be done automatically by the server.
            if (s.type === `delivery` && s.pickup_id && stopsToDelete.includes(s.pickup_id)) {
              // Filtering the pickup
              stopsToDelete = stopsToDelete.filter((sId) => sId !== s.pickup_id);
            }
          });

          deleteStops({ stopIds: stopsToDelete });
        }}
      />

      <StopsToDriverDialog
        type="assign"
        open={dialog === `assignToDriver`}
        drivers={planDrivers ?? []}
        setDialog={setDialog}
        handleInteractStops={(destinationDriver: `missing` | Driver) => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          if (destinationDriver === `missing`) {
            const unassignedStops = (planStops as Stop[]).filter((stop) => !stop.driver_id);
            updateDriverStops({
              driver: undefined,
              stops: stops as Stop[],
              index: unassignedStops.length,
            });
          } else {
            const driverIndex = (planDrivers ?? []).findIndex((driver: Driver) => driver.id === destinationDriver.id);
            const driverNewStops = destinationDriver.stops;

            // Sort the stops variable: if there is a pickup, it must be placed before its deivery.
            const pickupsFirst = (stops as Stop[]).sort((a: Stop, b: Stop) => {
              if (a.type === `pickup` && b.type === `delivery` && a.id === b.pickup_id) {
                return -1;
              }
              if (a.type === `delivery` && b.type === `pickup` && a.pickup_id === b.id) {
                return 1;
              }
              return 0;
            });

            pickupsFirst.forEach((stop) => {
              if (stop.type === `pickup`) {
                driverNewStops.push(stop);
                const delivery = pickupsFirst.find((s) => s.pickup_id === stop.id);
                if (delivery) {
                  driverNewStops.push(delivery);
                }
              } else if (!driverNewStops.find((driverNewStop: Stop) => driverNewStop.id === stop.id)) {
                driverNewStops.push(stop);
              }
            });
            updateDriverStops({ driver: destinationDriver, stops: driverNewStops, index: driverIndex });
          }
        }}
      />

      <MoveStopsDialog
        open={dialog === `moveStops`}
        currentPlanId={activePlan?.id}
        selectedStopIds={stops.map((stop) => stop.id)}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
      />

      <StopsToSupervisorDialog
        open={dialog === `assignToSupervisor`}
        stopIds={stops.map((stop) => stop.id)}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
      />

      {activeProject && (
        <ConstraintsDialog
          typeData="stops"
          open={dialog === `constraintsStops`}
          constraintsMap={[
            { id: `location`, type: `location` as ConstraintsType },
            ...(activeProject?.view?.constraints.time_windows
              ? [{ id: `time_windows`, type: `time_windows` as ConstraintsType }]
              : []),
            ...(stops.every((stop) => stop.type === `delivery`) && activeProject?.view?.constraints.weight
              ? [{ id: `weight`, type: `number` as ConstraintsType }]
              : []),
            ...(stops.every((stop) => stop.type === `delivery`) && activeProject?.view?.constraints.volume
              ? [{ id: `volume`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.provide_requires
              ? [{ id: `requires`, type: `text_array` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.cluster ? [{ id: `cluster`, type: `text` as ConstraintsType }] : []),
            ...(activeProject?.view?.constraints.max_delivery_time
              ? [{ id: `max_delivery_time`, type: `minutes` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.reward ? [{ id: `reward`, type: `number` as ConstraintsType }] : []),
            { id: `duration`, type: `minutes` as ConstraintsType },
            // { id: `cluster`, type: `text` as ConstraintsType }
          ]}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
          idsToUpdate={stops.map((stop) => stop.id)}
          numberOfElements={stops.length}
          onUpdateConstraints={(updatedStops: Stop[]) => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}

      {activeProject && (
        <ExportStopsReportsDialog
          open={dialog === `exportReports`}
          selectedStopIds={stops.map((stop) => stop.id)}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}

      {activeProject && (
        <ExportStopsTasksDialog
          open={dialog === `exportTasks`}
          selectedStopIds={stops.map((stop) => stop.id)}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}
    </>
  );
};
